import { Button } from '@components/Buttons/Button';
import { Card } from '@components/Cards/Card';
import { Field, Form, FormError } from '@components/Conform';
import type { LoginBody } from '../client';
import { useSession, useLogin } from '../hooks/api/session';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BasicCenteredLayout } from '@components/nibll/layouts/BasicCenteredLayout';
import { email } from '@components/util/schemas';

const loginSchema = z.object({
  email,
  password: z.string().min(1, { message: 'Please enter your password' }),
});

export const Login = () => {
  const [error, setError] = useState<string | undefined>(undefined);
  const { mutate, isLoading } = useLogin({
    onSuccess: () => navigate('/'),
    onError: () => setError('Invalid email or password'),
  });
  const { session, isLoading: sessionLoading, isRefetching } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (session && !sessionLoading && !isRefetching) navigate('/');
  }, [session, navigate]);

  if (sessionLoading) return null;

  return (
    <BasicCenteredLayout>
      <Card className="max-w-md mx-auto">
        <Card.Header>
          <Card.Title>Login</Card.Title>
        </Card.Header>

        <Form
          schema={loginSchema}
          error={error}
          onSubmit={(event, { submission }) => {
            // Note: Conform only calls this for a valid submission; the login request replaces the native post
            event.preventDefault();
            if (submission?.status !== 'success') return;
            setError(undefined);
            // Note: this app compiles without `strict`, so Zod infers every key as optional
            mutate(loginSchema.parse(submission.value) as LoginBody);
          }}
        >
          <Card.Content className="grid gap-4">
            <Field name="email" label="Email" />
            <Field name="password" type="password" label="Password" />
            <FormError />
          </Card.Content>
          <Card.Footer>
            <Card.Actions>
              <Button type="submit" disabled={isLoading}>
                Login
              </Button>
            </Card.Actions>
          </Card.Footer>
        </Form>
      </Card>
    </BasicCenteredLayout>
  );
};
